(() => {
  'use strict';
  // Native Dawn owns gallery/form/variant state. This route enhancement adds
  // readiness only after its usable markup exists; visible content never waits.
  if (window.__miniamiSourcePdp) return;
  window.__miniamiSourcePdp = true;
  // Normalize only rendered BR/marker paragraphs; merchant descriptionHtml stays untouched.
  const formatDescription = rte => {
    // Readiness is diagnostic, never a guard: editors can replace the same HTML.
    if (!rte) return;
    rte.dataset.sourceListsReady = 'true';
    rte.querySelectorAll('p,div').forEach(paragraph => {
      // Keep merchant containers and already-semantic lists intact; format leaf runs only.
      if (paragraph.querySelector('p,div,ul,ol,li')) return;
      const lines = [document.createDocumentFragment()];
      const visit = (node, ancestors = []) => {
        if (node.nodeType === Node.TEXT_NODE) {
          let copy = node.cloneNode();
          ancestors.slice().reverse().forEach(parent => { const wrapper = parent.cloneNode(); wrapper.append(copy); copy = wrapper; });
          lines[lines.length - 1].append(copy);
        } else if (node.nodeName === 'BR') lines.push(document.createDocumentFragment());
        else if (node.nodeType === Node.ELEMENT_NODE) node.childNodes.forEach(child => visit(child, [...ancestors, node]));
      };
      paragraph.childNodes.forEach(node => visit(node));
      if (!lines.some(line => /^\s*[*•-]\s+/.test(line.textContent))) return;
      const output = document.createDocumentFragment();
      let list = null, marker = '', parentItem = null, nested = null;
      lines.forEach(line => {
        const match = line.textContent.match(/^\s*([*•-])\s+/);
        if (!match) {
          list = null; nested = null; parentItem = null;
          if (!line.textContent.trim()) return;
          const p = paragraph.cloneNode(); p.append(line); output.append(p); return;
        }
        // Remove marker syntax only, including markers split across inline spans.
        let remaining = match[0].length;
        const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT);
        while (remaining) { const text = walker.nextNode(); if (!text) break; const take = Math.min(remaining, text.length); text.deleteData(0, take); remaining -= take; }
        const item = document.createElement('li'); item.append(line);
        if (match[1] === '-' && marker === '*' && parentItem) {
          if (!nested) { nested = document.createElement('ul'); parentItem.append(nested); }
          nested.append(item);
        } else {
          if (!list || marker !== match[1]) { list = document.createElement('ul'); output.append(list); }
          list.append(item); marker = match[1]; parentItem = item; nested = null;
        }
      });
      paragraph.replaceWith(output);
    });
  };
  // Native details remains the no-JS disclosure; this owner only adds reversible motion.
  if (!customElements.get('miniami-description-disclosure')) customElements.define('miniami-description-disclosure', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const details = this.querySelector('details'), summary = details?.querySelector('summary');
      if (!details || !summary) return;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      let animation = null, expanded = details.open, content = null;
      const clear = () => { animation?.cancel(); animation = null; if (content) { content.style.height = ''; content.style.overflow = ''; } };
      const settle = () => { clear(); details.open = expanded; if (content) content.inert = !expanded; summary.setAttribute('aria-expanded', String(expanded)); };
      const format = () => {
        const next = details.querySelector(':scope > .rte');
        if (!next) return;
        if (content !== next) { clear(); content = next; }
        const excerpt = this.parentElement?.querySelector(':scope > .rte');
        // Compare the exact native excerpt prefix (including mixed DIV/P markup).
        // DOM equality is read-only; no serialized HTML is assigned to production.
        const prefix = excerpt ? [...excerpt.childNodes] : [];
        if (prefix.length && prefix.every((node,index) => node.isEqualNode(content.childNodes[index]))) prefix.forEach(() => content.firstChild.remove());
        else if (excerpt && excerpt.textContent.trim() === content.textContent.trim()) {
          // DIV-only native descriptions make Liquid's </p> excerpt the whole file.
          // Move just its first native leaf block into the excerpt, retaining the rest once.
          let first = content.firstElementChild;
          while (first?.tagName === 'DIV' && first.firstElementChild?.matches('p,div')) first = first.firstElementChild;
          if (first) { excerpt.replaceChildren(first.cloneNode(true)); first.remove(); }
        }
        formatDescription(content);
      };
      const toggle = event => {
        event.preventDefault();
        // Chromium can retain layout boxes inside closed native details.
        const from = details.open ? (content?.getBoundingClientRect().height || 0) : 0;
        expanded = !expanded; clear();
        details.open = true;
        if (content) content.inert = !expanded;
        summary.setAttribute('aria-expanded', String(expanded));
        if (!content?.animate || reduce.matches) { settle(); return; }
        const to = expanded ? content.scrollHeight : 0;
        content.style.overflow = 'hidden';
        const active = content.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration:260, easing:'cubic-bezier(.2,.7,.2,1)' });
        animation = active;
        active.finished.then(() => { if (animation === active) settle(); }, () => {});
      };
      const nativeToggle = () => { if (!animation) { expanded = details.open; settle(); } };
      const observer = new MutationObserver(() => { format(); });
      format(); settle();
      observer.observe(details, { childList:true, subtree:true, characterData:true });
      summary.addEventListener('click', toggle); details.addEventListener('toggle', nativeToggle); reduce.addEventListener('change', settle);
      this.cleanup = () => { observer.disconnect(); clear(); summary.removeEventListener('click', toggle); details.removeEventListener('toggle', nativeToggle); reduce.removeEventListener('change', settle); this.cleanup = null; };
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
  if (!customElements.get('miniami-pairs-pager')) customElements.define('miniami-pairs-pager', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const cards = [...this.querySelectorAll('[data-paired-product-id]')].slice(0,12), grid = this.querySelector('.miniami-source-pairs__grid');
      const prev = this.querySelector('[data-pairs-prev]'), next = this.querySelector('[data-pairs-next]'), status = this.querySelector('[data-pairs-status]');
      if (!grid || !prev || !next) return;
      let page = 0, pointer = null, suppressClick = false;
      const pageSize = Math.max(1, Math.min(12, Number.parseInt(this.dataset.pageSize, 10) || 4));
      const count = Math.ceil(cards.length / pageSize);
      const show = index => {
        page = Math.max(0, Math.min(count - 1, index));
        cards.forEach((card,i) => { card.hidden = i < page * pageSize || i >= (page + 1) * pageSize; });
        prev.disabled = page <= 0; next.disabled = page >= count - 1;
        if (status) status.textContent = `Page ${page + 1} of ${count}`;
        if (cards.some(card => card.hidden && card.contains(document.activeElement))) cards[page * pageSize]?.querySelector('a')?.focus({ preventScroll:true });
      };
      const back = () => show(page - 1), forward = () => show(page + 1);
      const key = e => {
        if (pageSize !== 1 || e.target.closest('button,input,select,textarea') || !['ArrowLeft','ArrowRight'].includes(e.key)) return;
        e.preventDefault(); show(page + (e.key === 'ArrowRight' ? 1 : -1));
      };
      const down = e => { suppressClick = false; if (e.isPrimary && !e.target.closest('button,input,select,textarea')) pointer = { id:e.pointerId, x:e.clientX, y:e.clientY }; };
      const up = e => {
        if (!pointer || pointer.id !== e.pointerId) return;
        const dx = e.clientX - pointer.x, dy = e.clientY - pointer.y; pointer = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        suppressClick = true; show(page + (dx < 0 ? 1 : -1));
      };
      const cancel = () => { pointer = null; suppressClick = false; };
      const click = e => { if (suppressClick) { e.preventDefault(); e.stopPropagation(); suppressClick = false; } };
      prev.addEventListener('click', back); next.addEventListener('click', forward);
      grid.addEventListener('keydown', key);
      grid.addEventListener('pointerdown', down); grid.addEventListener('pointerup', up); grid.addEventListener('pointercancel', cancel); grid.addEventListener('click', click, true);
      show(0);
      this.cleanup = () => { prev.removeEventListener('click', back); next.removeEventListener('click', forward); grid.removeEventListener('keydown', key); grid.removeEventListener('pointerdown', down); grid.removeEventListener('pointerup', up); grid.removeEventListener('pointercancel', cancel); grid.removeEventListener('click', click, true); this.cleanup = null; };
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
  const bind = scope => {
    const roots = scope.matches?.('product-info[data-miniami-source-layout]') ? [scope] : [...scope.querySelectorAll('product-info[data-miniami-source-layout]')];
    roots.forEach(root => {
      root.querySelectorAll('.miniami-source-description__more > .rte').forEach(formatDescription);
      customElements.whenDefined('media-gallery').then(() => {
        if (root.isConnected) root.classList.add('miniami-source-scripted');
      });
      customElements.whenDefined('product-form').then(() => {
        if (root.isConnected) root.classList.add('miniami-source-pair-ready');
      });
      const gallery = root.querySelector('media-gallery');
      const active = gallery?.querySelector('[data-gallery-slide].is-active');
      if (active && gallery?.querySelectorAll('[data-gallery-slide]').length > 1) {
        const track = gallery.querySelector('[data-gallery-track]');
        if (track && active !== track.firstElementChild) track.scrollLeft = active.offsetLeft;
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bind(document), { once:true });
  else bind(document);
  document.addEventListener('shopify:section:load', e => bind(e.target));
})();
